import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { V2_CATALOG } from '@/features/v2screens/catalog';
import { newScreensTargetFor } from '@/shared/newscreens/newScreensMap';
import { useNewScreens, useNewScreensList } from '@/shared/newscreens/useNewScreens';

/**
 * slice-3690 -- the swap half of the "New Screens" slider (Chris, docs/demochanges.txt
 * 2026-09-17, item 2). Wraps the router's whole {@code <Routes>} tree ONCE, in
 * {@code app/router.tsx}, and sends the seven screens Chris named to their v2 page.
 *
 * <p>WHY A WRAPPER AND NOT SEVEN WRAPPED ROUTES: two of the seven -- `/browse/order-tag-search`
 * and `/browse/cfg-locations` -- have no route of their own. They resolve through the generic
 * `/browse/:key` route into `TableBrowserPage`, so there is no element to wrap without wrapping
 * every browse key. One wrapper above `<Routes>` covers all seven, and because it renders the
 * redirect INSTEAD of the tree, the faithful page never mounts and never fires its queries.
 *
 * <p>NO LOOP: the mapping's keys are the seven faithful routes only. A v2 route is never a key,
 * so a v2 page can never redirect back. A user who turns the slider off while standing on a v2
 * page simply stays there until they navigate -- this component only acts on the seven.
 *
 * <p>The query string and hash ride along (e.g. `/browse/trucks?new=1`), so a drill-in link that
 * seeds criteria keeps its criteria. The redirect is `replace`, so Back still goes where the
 * operator came from instead of bouncing through the faithful route.
 *
 * <p>DEMO ONLY (ADR-0050): {@code demo} is false on ph10-test and MYRX, so this renders
 * {@code children} untouched there and the faithful routes behave exactly as before.
 */
export function NewScreensSwap({ children }: { children: ReactNode }) {
  const { enabled, demo } = useNewScreens();
  // slice-4481 (John 29 Oct): the tenant's own list of v2 pages (v2 Screens page checklist);
  // never saved = Chris's seven, as before.
  const list = useNewScreensList(demo && enabled);
  const location = useLocation();

  // slice-3695 (RUSS on demo, 2026-09-18: "can't access manage truck to ship the truck"): a URL
  // that carries a QUERY STRING is a caller-scoped open -- PB's New Truck menu item lands on
  // /browse/trucks?new=1 with a blank row, zoom drills arrive with ?warehouseId&truckId -- and
  // the v2 pages document that those stay on the faithful page (they have no New Truck form).
  // The swap redirected them anyway, so "New Truck" dead-ended on Truck Dispatch v2 with no
  // truck to focus and every action greyed "select a truck first". Bare opens still swap.
  const callerScoped = location.search !== '';
  const active = demo && enabled && !callerScoped;
  // While the list is loading, hold a bare open of any route a v2 page could replace, so neither
  // the PowerHouse page (and its queries) nor the wrong v2 page flashes up first.
  if (active && !list.ready && newScreensTargetFor(location.pathname, V2_CATALOG, V2_CATALOG.map((e) => e.route))) {
    return null;
  }
  const target = active ? newScreensTargetFor(location.pathname, V2_CATALOG, list.routes) : null;

  if (target) {
    return <Navigate to={`${target}${location.hash}`} replace />;
  }
  return <>{children}</>;
}
